// @ts-check

// Vendor imports
const classNames = require('classnames')
const _ = require('lodash')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const { formatCount } = require('shared/string-utils')
const { TYPES: modelTypes } = require('shared/model-types')
// Client V2 common imports
const { CircleButton } = require('client-v2/common/components/buttons')
const EmbeddedPlayer = require('client-v2/common/components/embedded-player')
const LazyImage = require('client-v2/common/components/lazy-image')
const UserAvatar = require('client-v2/common/components/user-avatar')
const Icon = require('client-v2/common/containers/icons/component')
const ArticleMixButton = require('client-v2/common/containers/lightweight-engagement/components/article-mix-button')
const AddToCollectionModalAnchor = require('client-v2/common/containers/lightweight-engagement/containers/add-to-collection-modal/components/anchor-smart')
const { usePropsCallback, usePropsEventCallback } = require('client-v2/common/hooks/callbacks')
const useLineClampFitStyle = require('client-v2/common/hooks/line-clamp')
const useSymbol = require('client-v2/common/hooks/symbol')
const {
  use10PctVisibleViewportObserver,
  use100PctVisibleViewportObserver
} = require('client-v2/common/hooks/viewport-observer')
const articleModelActions = require('client-v2/common/models/article/actions')
const { stopPropagation } = require('client-v2/common/utils/events')
const tag = require('client-v2/common/utils/hyperscript')

/** @typedef { import('client-v2/common/components/portal').PortalContainer } PortalContainer */
/** @typedef { import('client-v2/common/models/article/types').ArticleModel } ArticleModel */
/** @typedef { import('client-v2/common/models/page-context/types').PageContextModel } PageContextModel */
/** @typedef { import('client-v2/common/containers/feed/types').RecReason } RecReason */

/**
 * @typedef {Object} ArticleCardProps
 * Flags
 * @property {boolean} isCountVisible
 * @property {boolean} isHrefsEnabled
 * Values
 * @property {ArticleModel} article
 * @property {string} [className]
 * @property {number} [itemIndex]
 * @property {PageContextModel} pageContext
 * @property {PortalContainer} portalContainer
 * @property {RecReason} [recReason]
 * Callbacks
 * @property {function} onAddClick
 * @property {function} onCardClick
 * @property {function} onCountClick
 * @property {function} onDomainClick
 * @property {function} onEmbedDisclose
 * @property {function} onMoreClick
 * @property {function} onPreviewClick
 * @property {function} onSeen
 * @property {function} onShareClick
 */

/**
 * @typedef {Object} ArticleCardContentProps
 * @property {boolean} isFullyVisible
 * @property {boolean} isPartiallyVisible
 */

/**
 * @typedef {Object} ArticleCardAddButtonProps
 * @property {Symbol} anchorId
 */

const bem = BemEntity('ArticleCard')

/**
 * @param {ArticleCardProps} props
 */
function ArticleCard(props) {
  // Hooks
  const blockRef = React.useRef()
  const anchorId = useSymbol()
  const onCardClick = usePropsEventCallback(props, 'onCardClick')
  use100PctVisibleViewportObserver({
    elementRef: blockRef,
    unobserveOnVisible: true,
    onVisible: usePropsCallback(props, 'onSeen')
  })
  const [ isPartiallyVisible ] = use10PctVisibleViewportObserver({
    elementRef: blockRef,
    unobserveOnVisible: false
  })

  return (
    tag('div', {
      ref: blockRef,
      className: classNames(
        props.className,
        bem.block({
          'has-count': props.isCountVisible && !!props.article.count,
          'has-rec-reason': !!props.recReason,
          'hrefs-enabled': props.isHrefsEnabled,
          interactive: !!props.onCardClick
        })
      ),
      dataset: {
        urlId: props.article['url_id']
      }
    },
      tag('div', {
        className: bem.element('inner'),
        onClick: onCardClick
      },
        tag(ArticleCardContent, {
          ...props,
          isPartiallyVisible
        }),
        tag(ArticleCardFooter, props),
        tag(ArticleCardCount, props),
        tag('div', {
          className: bem.element('bottom-actions')
        },
          tag(ArticleCardAddButton, {
            ...props,
            anchorId
          }),
          tag(ArticleCardShareButton, props)
        )
      )
    )
  )
}

ArticleCard.defaultProps = {
  isCountVisible: true,
  isHrefsEnabled: true
}

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')
  const Portal = require('client-v2/common/components/portal')
  const { RecReasonRecord } = require('client-v2/common/containers/feed/records')
  const ArticleModelRecord = require('client-v2/common/models/article/record')
  const { PageContextOverlayRecord } = require('client-v2/common/models/page-context/records')

  ArticleCard.propTypes = {
    // Flags
    isCountVisible: PropTypes.bool,
    isHrefsEnabled: PropTypes.bool,
    // Values
    article: PropTypes.shape(ArticleModelRecord.propTypes).isRequired,
    className: PropTypes.string,
    itemIndex: PropTypes.number,
    pageContext: PropTypes.shape(PageContextOverlayRecord.propTypes),
    portalContainer: Portal.containerPropType.isRequired,
    recReason: PropTypes.shape(RecReasonRecord.propTypes),
    // Callbacks
    onAddClick: PropTypes.func,
    onCardClick: PropTypes.func,
    onCountClick: PropTypes.func,
    onDomainClick: PropTypes.func,
    onEmbedDisclose: PropTypes.func,
    onMoreClick: PropTypes.func,
    onPreviewClick: PropTypes.func,
    onSeen: PropTypes.func,
    onShareClick: PropTypes.func
  }
}

module.exports = React.memo(ArticleCard)

// BEM Block entity Element components

/**
 * @param {ArticleCardProps & ArticleCardContentProps} props
 */
function ArticleCardContent(props) {
  const { embedUrl, image, md5image } = props.article
  if (embedUrl) {
    return tag([
      tag(ArticleCardEmbed, props),
      tag(ArticleCardDetails, props)
    ])
  }
  if (image || md5image) {
    return tag([
      tag(ArticleCardImage, props),
      tag(ArticleCardDetails, props)
    ])
  }
  return tag([
    tag(ArticleCardDetails, props)
  ])
}

/**
 * @param {ArticleCardProps & ArticleCardContentProps} props
 */
function ArticleCardDetails(props) {
  return (
    tag('div', {
      className: bem.element('details')
    },
      tag('div', {
        className: bem.element('top-actions')
      },
        tag(ArticleCardDomain, props),
        tag(ArticleCardPreviewButton, props),
        tag(ArticleCardMoreButton, props)
      ),
      tag(ArticleCardTitle, props),
      tag(ArticleCardDescription, props)
    )
  )
}

/**
 * @param {ArticleCardProps & ArticleCardContentProps} props
 */
function ArticleCardTitle(props) {
  const ref = React.useRef()
  const style = useLineClampFitStyle({
    disabled: !props.isPartiallyVisible,
    domRef: ref
  })
  return (
    tag('a', {
      ref,
      className: bem.element('title'),
      href: getArticleContentHref(props),
      rel: 'external nofollow noopener',
      style,
      target: '_blank'
    },
      props.article.title
    )
  )
}

/**
 * @param {ArticleCardProps & ArticleCardContentProps} props
 */
function ArticleCardDescription(props) {
  const { description } = props.article
  const ref = React.useRef()
  const style = useLineClampFitStyle({
    disabled: !description || !props.isPartiallyVisible,
    domRef: ref
  })
  if (!description) {
    return null
  }
  return (
    tag('a', {
      ref,
      className: bem.element('description'),
      href: getArticleContentHref(props),
      rel: 'external nofollow noopener',
      style,
      target: '_blank'
    },
      description
    )
  )
}

/**
 * @param {ArticleCardProps} props
 */
function ArticleCardDomain(props) {
  const onClick = usePropsEventCallback(props, 'onDomainClick')
  return (
    tag('a', {
      className: bem.element('domain'),
      href: getDomainSearchHref(props),
      onClick
    },
      props.article.hostname
    )
  )
}

/**
 * @param {ArticleCardProps} props
 */
function ArticleCardPreviewButton(props) {
  const onClick = usePropsEventCallback(props, 'onPreviewClick')
  if (!props.onPreviewClick) {
    return null
  }
  return (
    tag(CircleButton, {
      className: bem.element('preview-button'),
      href: getArticleProfileHref(props),
      title: 'Get more details about this post',
      onClick
    },
      tag(Icon, { id: 'fullscreen' })
    )
  )
}

/**
 * @param {ArticleCardProps} props
 */
function ArticleCardMoreButton(props) {
  const onClick = usePropsEventCallback(props, 'onMoreClick')
  if (props.article.isMixed || !props.onMoreClick) {
    return null
  }
  return (
    tag(CircleButton, {
      className: bem.element('more-button'),
      title: 'Report this content or block the source',
      onClick
    },
      tag(Icon, { id: 'vertical-ellipsis' })
    )
  )
}

/**
 * @param {ArticleCardProps} props
 */
function ArticleCardEmbed(props) {
  const onDisclose = usePropsCallback(props, 'onEmbedDisclose')
  const { embedUrl, url } = props.article
  if (!embedUrl) {
    return tag(ArticleCardImage, props)
  }
  return (
    tag('div', {
      className: bem.element('embed-container')
    },
      tag(ArticleCardImage, props),
      tag(EmbeddedPlayer, {
        className: bem.element('embed'),
        src: embedUrl,
        fallbackUrl: url,
        onDisclose
      })
    )
  )
}

/**
 * @param {ArticleCardProps} props
 */
function ArticleCardImage(props) {
  const { article: { image, md5image } } = props
  const sources = React.useMemo(
    () => [ image, md5image ],
    [ image, md5image ]
  )
  return (
    tag(LazyImage, {
      className: bem.element('image'),
      href: getArticleContentHref(props),
      rel: 'external nofollow noopener',
      target: '_blank',
      sources
    })
  )
}

/**
 * @param {ArticleCardProps & ArticleCardAddButtonProps} props
 */
function ArticleCardAddButton(props) {
  const onClick = usePropsEventCallback(props, 'onAddClick')
  if (!props.onAddClick) {
    return null
  }
  return (
    tag(AddToCollectionModalAnchor, {
      anchorId: props.anchorId,
      popperClassName: bem.element('add-button-popper'),
      portalContainer: props.portalContainer,
      urlId: props.article['url_id']
    },
      tag(ArticleMixButton, {
        className: bem.element('add-button'),
        checked: props.article.isMixed,
        onClick
      })
    )
  )
}

/**
 * @param {ArticleCardProps} props
 */
function ArticleCardShareButton(props) {
  const onClick = usePropsEventCallback(props, 'onShareClick')
  if (!props.onShareClick) {
    return null
  }
  return (
    tag(CircleButton, {
      className: bem.element('share-button'),
      title: 'Share this post with a friend',
      onClick
    },
      tag(Icon, { id: 'share-send' })
    )
  )
}

/**
 * @param {ArticleCardProps} props
 */
function ArticleCardFooter(props) {
  const recReasonHtml = _.get(props, ['recReason', 'html'])
  if (!recReasonHtml) {
    return (
      tag('div', {
        className: bem.element('footer')
      })
    )
  }
  return (
    tag('div', {
      className: bem.element('footer'),
      onClick: stopPropagation
    },
      tag(ArticleCardFooterIcon, props),
      tag('span', {
        className: bem.element('footer-text'),
        dangerouslySetInnerHTML: { __html: recReasonHtml }
      })
    )
  )
}

/**
 * @param {ArticleCardProps} props
 */
function ArticleCardFooterIcon(props) {
  const firstRecReasonItem = _.get(props, ['recReason', 'items', 0])
  if (firstRecReasonItem && firstRecReasonItem.type === modelTypes.USER) {
    return (
      tag(UserAvatar, {
        className: bem.element('footer-icon'),
        badge: false,
        href: true,
        user: firstRecReasonItem
      })
    )
  }
  return null
}

/**
 * @param {ArticleCardProps} props
 */
function ArticleCardCount(props) {
  const onClick = usePropsEventCallback(props, 'onCountClick')
  if (!props.isCountVisible || !props.article.count) {
    return null
  }
  return (
    tag('a', {
      className: bem.element('count'),
      href: getArticleProfileHref(props),
      onClick
    },
      `${formatCount(props.article.count)}`
    )
  )
}

// Linkage helpers

/**
 * @param {ArticleCardProps} props
 * @returns {string}
 */
function getArticleContentHref(props) {
  if (props.isHrefsEnabled) {
    return props.article.url
  }
}

/**
 * @param {ArticleCardProps} props
 * @returns {string}
 */
function getDomainSearchHref(props) {
  if (props.isHrefsEnabled) {
    return articleModelActions.articleDomainSearchHref(props.article)
  }
}

/**
 * @param {ArticleCardProps} props
 * @returns {string}
 */
function getArticleProfileHref(props) {
  if (props.isHrefsEnabled) {
    return articleModelActions.articleProfileHref(props.article)
  }
}
